import Modal from 'react-bootstrap/Modal';
import PrimaryButton from '../../components/button';
import TextField from '../../components/textFiled';
import { useEffect, useRef, useState } from 'react';
import Selectfield from '../../components/selectfield';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  useCreateMenuItemMutation,
  useUpdateMenuItemMutation,
  useUploadMenuItemImageMutation,
  useDeleteMenuItemImageMutation,
  useFetchCategoriesQuery,
} from '../../api';

type Props = {
  show: boolean;
  hide: () => void;
  details?: any;
  // Optional setter passed from parent so we can update local details (e.g. after deleting an image)
  setDetails?: (details: any) => void;
};

function CreateMenuItemModal({ show, hide, details, setDetails }: Props) {
  const [createMutation, { isLoading }] = useCreateMenuItemMutation();
  const [updateMutation, { isLoading: updateLoading }] = useUpdateMenuItemMutation();
  const [uploadImageMutation, { isLoading: isAddingImage }] = useUploadMenuItemImageMutation();
  const [deleteImageMutation] = useDeleteMenuItemImageMutation();

  const [values, setValues] = useState({
    category: '',
    categoryId: '',
    name: '',
    price: '',
    description: '',
    ingredients: '',
    preparationTime: '',
    isAvailable: undefined as boolean | undefined,
    stockQuantity: ''
  });

  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);

  const onDrop = (acceptedFiles: File[]) => {
    // Only take the first file if multiple are selected
    const file = acceptedFiles[0];
    if (!file) return;
    
    const toBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
      });
    };

    // Clear existing previews and files
    setPreviews([]);
    setNewFiles([]);
    
    // Convert the new file to base64 and update state
    toBase64(file).then((base64Image) => {
      setPreviews([base64Image]);
      setNewFiles([file]);
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false, // Only allow one file at a time
    maxFiles: 1 // Explicitly set max files to 1
  });

  const removeNewImage = (index: number) => {
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  // categories from backend (store id + name)
  const [categories, setCategories] = useState<any[]>([]);
  const { data: fetchedCategories } = useFetchCategoriesQuery('');

  useEffect(() => {
    if (show && fetchedCategories) {
      try {
        const list = Array.isArray(fetchedCategories) ? fetchedCategories : [];
        setCategories(list);
      } catch {}
    }

    if (show && !details) {
      setValues({
        category: '',
        categoryId: '',
        name: '',
        price: '',
        description: '',
        ingredients: '',
        preparationTime: '',
        isAvailable: undefined,
        stockQuantity: ''
      });
      setNewFiles([]);
      setPreviews([]);
    } else if (show && details) {
      setValues({
        category: details?.type || '',
        categoryId: details?.categoryId || '',
        name: details?.deviceName || '',
        price: details?.price || '',
        description: details?.description || '',
        ingredients: details?.ingredients || '',
        preparationTime: details?.preparationTime || '',
        isAvailable: details?.isAvailable !== undefined ? details.isAvailable : true,
        stockQuantity: details?.stockQuantity || ''
      });
    }
  }, [show, details, fetchedCategories]);


  const handleDeleteExistingImage = async (imageId: string) => {
    try {
      await deleteImageMutation(imageId).unwrap();
      // Update the details to remove the deleted image
      if (details?.images && setDetails) {
        setDetails({
          ...details,
          images: details.images.filter((img: any) => img._id !== imageId)
        });
      }
      toast.success('Image deleted');
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to delete image');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // ✅ Basic validation with toast messages
    if (!values.category) {
      toast.error('Please select a category');
      return;
    }
    if (!values.name.trim()) {
      toast.error('Please enter an item name');
      return;
    }
    if (!values.price || Number(values.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    if (!values.stockQuantity || Number(values.stockQuantity) < 0) {
      toast.error('Please enter a valid stock quantity');
      return;
    }

    const payload = {
      type: values.category,
      categoryId: values.categoryId || undefined,
      deviceName: values.name,
      price: Number(values.price),
      description: values.description,
      ingredients: values.ingredients,
      preparationTime: values.preparationTime,
      isAvailable: values.isAvailable,
      stockQuantity: Number(values.stockQuantity),
    };

    try {
      if (details && details._id) {
        // 🟦 Update existing item
        const updateResult: any = await updateMutation({
          id: details._id,
          body: payload
        }).unwrap();

        // Upload images if any were added
        if (newFiles.length && updateResult._id) {
          for (const file of newFiles) {
            const formdata = new FormData();
            formdata.append('file', file);
            formdata.append('menuItemId', updateResult._id);
            await uploadImageMutation(formdata).unwrap();
          }
        }

        toast.success('Menu item updated successfully!');
        hide();
      } else {
        // 🟩 Create new item
        const createResult: any = await createMutation(payload).unwrap();

        // Upload images if any were added
        if (newFiles.length && createResult._id) {
          for (const file of newFiles) {
            const formdata = new FormData();
            formdata.append('file', file);
            formdata.append('menuItemId', createResult._id);
            await uploadImageMutation(formdata).unwrap();
          }
        }

        toast.success('Menu item added successfully!');
        hide();
      }
    } catch (err: any) {
      console.error('Error saving menu item:', err);
      const errorMessage = err?.data?.error || err?.data?.message || err?.message || 'Error saving menu item. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <Modal show={show} onHide={hide} size="lg" centered>
      <Modal.Body>
        <div className='customer-details-title-ctn'>
          <span className='customer-details-title'>
            {details && details._id ? 'Edit Menu Item' : 'Add New Menu Item'}
          </span>
        </div>
        
        <div className='modal-details-content'>
          <form onSubmit={handleSubmit} noValidate>
            <Selectfield
              menuItems={categories.map((c: any) => ({ label: c.name, value: c._id }))}
              label='Category'
              value={values.categoryId}
              customRenderValue={(selectedId) => {
                if (!selectedId) return <em>Select a category</em>;
                const found = categories.find((c: any) => c._id === selectedId);
                return found?.name || selectedId;
              }}
              handleChange={(e: any) => {
                const selectedId = e.target.value;
                const found = categories.find((c: any) => c._id === selectedId);
                setValues({
                  ...values,
                  categoryId: selectedId,
                  category: found?.name || ''
                })
              }}
              required
              placeholder="Select a category"
            />

            <TextField
              label='Item Name'
              value={values.name}
              onChange={(e: any) => setValues({
                ...values,
                name: e.target.value
              })}
              required
              type="text"
              placeholder="Enter item name"
            />

            <TextField
              label='Price'
              value={values.price}
              onChange={(e: any) => setValues({
                ...values,
                price: e.target.value
              })}
              type="number"
              required
              placeholder="Enter price in Rwf"
            />

            <TextField
              label='Stock Quantity'
              value={values.stockQuantity}
              onChange={(e: any) => setValues({
                ...values,
                stockQuantity: e.target.value
              })}
              type="number"
              required
              placeholder="Enter initial stock quantity"
            />

            <Selectfield
              menuItems={[
                { label: 'Available', value: 'Available' },
                { label: 'Not Available', value: 'Not Available' }
              ]}
              label='Availability'
              value={
                values.isAvailable === undefined
                  ? ''
                  : values.isAvailable
                  ? 'Available'
                  : 'Not Available'
              }
              handleChange={(e: any) => setValues({
                ...values,
                isAvailable: e.target.value === 'Available'
              })}
              required
              placeholder="Select availability"
            />

            {/* Images section moved to the bottom, similar to Rentals */}
            <div className='mt-6'>
              <span className='specifications-title'>Images</span>

              {/* Existing Images (when editing) */}
              {details && details._id && Array.isArray(details.images) && details.images.length > 0 && (
                <div className="image-preview-list mt-2">
                  {details.images.map((img: any, index: number) => (
                    <div key={img._id || index} className="details-image-container" style={{ position: 'relative' }}>
                      <img src={`${process.env.REACT_APP_MENU_IMAGE_URL}${img.url.replace(/^\/uploads/, "")}`} alt={`img-${index}`} className="image-thumb" />
                      <DeleteIcon
                        style={{ cursor: 'pointer', color: '#FF0000' }}
                        className="delete-icon" onClick={() => handleDeleteExistingImage(img._id)} />
                    </div>
                  ))}
                </div>
              )}

              {/* New image upload - Dropzone (similar to Rentals) */}
              <div className="dropzone mt-10" {...getRootProps()}>
                <input {...getInputProps()} />
                <div className={`upload-box ${isDragActive ? 'active' : ''}`}>
                  {isDragActive ? (
                    <p>Drop the images here ...</p>
                  ) : (
                    <p>Drag & drop images here, or click to select</p>
                  )}
                </div>
              </div>

              {/* Preview newly selected images */}
              {previews.length > 0 && (
                <div className="image-preview-list">
                  {previews.map((img: string, index: number) => (
                    <div key={index} className="details-image-container">
                      <img src={img} alt={`img-${index}`} className="image-thumb" />
                      <DeleteIcon
                        style={{ cursor: 'pointer', color: '#FF0000' }}
                        className="delete-icon" onClick={() => removeNewImage(index)} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <br />
            <br />
            <div className='w-1/2'>
              <PrimaryButton
                label={details && details._id ? 'Update' : 'Submit'}
                disabled={isLoading || updateLoading || isAddingImage}
                type="submit"
              />
            </div>
          </form>
        </div>
      </Modal.Body>

      {/* ✅ Toast container for notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Modal>
  );
}

export default CreateMenuItemModal;
